import axios from "axios";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";

const API_BASE_URL = import.meta.env.VITE_UTILS_API_URL;

export interface Currency {
  id: string;
  slug: string;
  code: string;
  name: string;
  symbol: string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCurrencyData {
  slug: string;
  name: string;
  code: string;
  symbol: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface UpdateCurrencyData extends Partial<CreateCurrencyData> {}

export interface CurrenciesResponse {
  success: boolean;
  data: Currency[];
  count: number;
}

export interface CurrencyResponse {
  success: boolean;
  data: Currency;
}

export interface CurrencyQueryParams {
  slug: string;
  is_active?: boolean;
}

export const utilsApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

utilsApi.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

utilsApi.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.statusCode === 201) &&
      response.data.body
    ) {
      response.data = response.data.body;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.hash = "#/login";
    }

    if (error.response?.data?.body?.message) {
      error.message = error.response.data.body.message;
    }

    return Promise.reject(error);
  },
);

export const currencyAPI = {
  /**
   * Obtiene lista de monedas
   */
  getCurrencies: async (
    params: CurrencyQueryParams,
  ): Promise<CurrenciesResponse> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("slug", params.slug);

      if (params.is_active !== undefined) {
        queryParams.append("is_active", params.is_active ? "1" : "0");
      }

      const response = await utilsApi.get(
        `/currencies?${queryParams.toString()}`,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          count: response.data.count,
        };
      } else {
        throw new Error(response.data?.message || "Error al obtener monedas");
      }
    } catch (error: any) {
      console.error("❌ Error en getCurrencies:", error);
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtiene una moneda por ID
   */
  getCurrencyById: async (
    id: string,
    slug: string,
  ): Promise<CurrencyResponse> => {
    try {
      const response = await utilsApi.get(`/currencies/${id}?slug=${slug}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.message || "Error al obtener la moneda");
      }
    } catch (error: any) {
      console.error("❌ Error en getCurrencyById:", error);
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Crea una nueva moneda
   */
  createCurrency: async (
    currencyData: CreateCurrencyData,
  ): Promise<CurrencyResponse> => {
    try {
      const response = await utilsApi.post(`/currencies`, currencyData);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.message || "Error al crear la moneda");
      }
    } catch (error: any) {
      console.error("❌ Error en createCurrency:", error);
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Actualiza una moneda existente
   */
  updateCurrency: async (
    id: string,
    currencyData: UpdateCurrencyData,
  ): Promise<CurrencyResponse> => {
    try {
      const { slug, ...updateData } = currencyData;

      if (!slug) {
        throw new Error("El slug es requerido para actualizar la moneda");
      }

      const response = await utilsApi.put(
        `/currencies/${id}?slug=${slug}`,
        updateData,
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        throw new Error(
          response.data?.message || "Error al actualizar la moneda",
        );
      }
    } catch (error: any) {
      console.error("❌ Error en updateCurrency:", error);
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },

  /**
   * Elimina una moneda
   */
  deleteCurrency: async (
    id: string,
    slug: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await utilsApi.delete(
        `/currencies/${id}?slug=${slug}`,
      );

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || "Moneda eliminada exitosamente",
        };
      } else {
        throw new Error(
          response.data?.message || "Error al eliminar la moneda",
        );
      }
    } catch (error: any) {
      console.error("❌ Error en deleteCurrency:", error);
      if (error.response) {
        throw new Error(error.response.data?.message || "Error en el servidor");
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error("Error al configurar la solicitud");
      }
    }
  },
};
